'use client';

/**
 * FlowTime — состояние приложения и движок таймера.
 *
 * Отсчёт ведётся по стенным часам (`Date.now`), а не сложением тиков.
 * Причина: в фоновой вкладке браузер прореживает таймеры до одного раза
 * в минуту, а при уходе ноутбука в сон они не срабатывают вовсе. Счётчик
 * тиков в обоих случаях отстаёт, и получасовая сессия записалась бы как
 * пятиминутная.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_SETTINGS,
  FLOW_PALETTE,
  emptyData,
  flowUid,
  loadFlowData,
  migrate,
  saveFlowData,
  type FlowData,
  type FlowEntry,
  type FlowProject,
  type FlowSession,
  type FlowSettings,
  type FlowTask,
  type TimerMode,
} from '@/lib/flowtime-storage';
import { playAlarm, syncAmbient, unlockAudio } from '@/lib/flowtime-audio';
import { clamp, dayKey } from '@/lib/flowtime-format';

/**
 * Названия режимов вынесены в переводы (`dh_flowtime_ui`).
 *
 * Здесь остаются только ключи: хук не знает языка, а компоненты получают
 * подписи через `useTranslations`.
 */
export const MODE_KEY: Record<TimerMode, string> = {
  focus: 'focus',
  short: 'short',
  long: 'long',
  free: 'free',
};

export function isBreak(mode: TimerMode): boolean {
  return mode === 'short' || mode === 'long';
}

/** Сессии короче этого порога не записываются: это случайные нажатия. */
const MIN_LOGGED_MS = 30_000;

export interface CompletionEvent {
  from: TimerMode;
  to: TimerMode;
  /** Дошла до конца сама, а не остановлена человеком. */
  natural: boolean;
  /** Интервал истёк, пока вкладка была закрыта. */
  offline: boolean;
  workedMs: number;
  entry: FlowEntry | null;
}

export function useFlowTime() {
  const [data, setData] = useState<FlowData>(emptyData);
  const [ready, setReady] = useState(false);
  const [recovered, setRecovered] = useState(false);
  /** Пересчитывает отображаемое время раз в секунду, не трогая хранилище. */
  const [, setPulse] = useState(0);

  const dataRef = useRef(data);
  dataRef.current = data;

  const completionRef = useRef<((e: CompletionEvent) => void) | null>(null);
  const onCompletion = useCallback((fn: (e: CompletionEvent) => void) => {
    completionRef.current = fn;
  }, []);

  // -------------------------------------------------------------------------
  // Загрузка и сохранение
  // -------------------------------------------------------------------------

  useEffect(() => {
    const loaded = loadFlowData();
    setData(loaded.data);
    setRecovered(loaded.recovered);
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    saveFlowData(data);
  }, [data, ready]);

  // Вторая вкладка того же браузера пишет в тот же ключ. Без этого две
  // вкладки затирали бы записи друг друга.
  useEffect(() => {
    if (!ready) return;
    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== 'dh.flowtime.v1' || !ev.newValue) return;
      try {
        setData(migrate(JSON.parse(ev.newValue)));
      } catch {
        /* чужая запись битая — своя остаётся в силе */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [ready]);

  const settings = data.settings;
  const session = data.session;

  const plannedFor = useCallback(
    (mode: TimerMode, s: FlowSettings = settings): number => {
      if (mode === 'focus') return s.focusMin * 60_000;
      if (mode === 'short') return s.shortMin * 60_000;
      if (mode === 'long') return s.longMin * 60_000;
      return 0; // свободный режим считает вверх, планового конца нет
    },
    [settings],
  );

  const freshSession = useCallback(
    (mode: TimerMode, taskId: string | null, s: FlowSettings = settings): FlowSession => {
      const task = taskId ? dataRef.current.tasks.find((t) => t.id === taskId) : undefined;
      return {
        mode,
        taskId: taskId,
        projectId: task?.projectId ?? null,
        startedAt: null,
        plannedMs: plannedFor(mode, s),
        elapsedMs: 0,
        running: false,
        lastResume: null,
        interruptions: 0,
        note: '',
      };
    },
    [plannedFor, settings],
  );

  // -------------------------------------------------------------------------
  // Производные величины
  // -------------------------------------------------------------------------

  /**
   * Пройденное время: накопленное плюс текущий отрезок.
   *
   * Считается на каждой отрисовке, без useMemo. Запомненное значение здесь
   * не годится: пока таймер идёт, объект сессии в хранилище не меняется,
   * и мемоизация возвращала бы то же число до самой паузы — циферблат
   * замирал на старте и оживал только после нажатия «Пауза».
   */
  const elapsedMs = session
    ? session.elapsedMs +
      (session.running && session.lastResume ? Date.now() - session.lastResume : 0)
    : 0;

  const remainingMs = session
    ? session.mode === 'free'
      ? elapsedMs
      : Math.max(0, session.plannedMs - elapsedMs)
    : plannedFor('focus');

  const progress = session
    ? session.mode === 'free'
      ? (elapsedMs % 3_600_000) / 3_600_000
      : session.plannedMs
        ? clamp(elapsedMs / session.plannedMs, 0, 1)
        : 0
    : 0;

  const running = !!session?.running;
  const active = !!session && (session.running || session.elapsedMs > 0);
  const mode: TimerMode = session?.mode ?? 'focus';

  // -------------------------------------------------------------------------
  // Запись сессии в журнал
  // -------------------------------------------------------------------------

  /**
   * Закрывает сессию и возвращает новое состояние.
   *
   * Чистая функция от предыдущего состояния: вызывается внутри setData,
   * чтобы два быстрых события (истёк таймер и нажата кнопка) не записали
   * одну сессию дважды.
   */
  const bankInto = useCallback(
    (
      prev: FlowData,
      completed: boolean,
    ): { next: FlowData; entry: FlowEntry | null; workedMs: number } => {
      const s = prev.session;
      if (!s) return { next: prev, entry: null, workedMs: 0 };

      const now = Date.now();
      const workedMs = s.elapsedMs + (s.running && s.lastResume ? now - s.lastResume : 0);
      const keep = isBreak(s.mode) ? prev.settings.logBreaks : true;

      if (workedMs < MIN_LOGGED_MS || !keep) {
        return { next: prev, entry: null, workedMs };
      }

      const entry: FlowEntry = {
        id: flowUid(),
        start: now - workedMs,
        end: now,
        mode: s.mode,
        taskId: s.taskId,
        projectId:
          s.projectId ??
          (s.taskId ? (prev.tasks.find((t) => t.id === s.taskId)?.projectId ?? null) : null),
        note: s.note,
        interruptions: s.interruptions,
        completed,
        manual: false,
      };

      return {
        next: { ...prev, entries: [...prev.entries, entry].sort((a, b) => a.start - b.start) },
        entry,
        workedMs,
      };
    },
    [],
  );

  /**
   * Переход к следующему интервалу.
   *
   * `natural` отличает истёкший таймер от нажатия «дальше»: только
   * доведённый до конца фокус двигает счётчик цикла к длинному перерыву.
   */
  const complete = useCallback(
    (natural: boolean, opts: { silent?: boolean; offline?: boolean } = {}) => {
      const prev = dataRef.current;
      const s = prev.session;
      if (!s) return;

      const { next, entry, workedMs } = bankInto(prev, natural);
      const fromMode = s.mode;
      let cyclePos = prev.cyclePos;
      let toMode: TimerMode = 'focus';

      if (fromMode === 'focus') {
        if (natural) {
          const pos = cyclePos + 1;
          const longDue = pos % prev.settings.longEvery === 0;
          cyclePos = longDue ? 0 : pos;
          toMode = longDue ? 'long' : 'short';
        } else {
          toMode = 'short';
        }
      } else if (fromMode === 'free') {
        toMode = 'free';
      }

      const task = s.taskId ? next.tasks.find((t) => t.id === s.taskId) : undefined;
      const fresh: FlowSession = {
        mode: toMode,
        taskId: s.taskId,
        projectId: task?.projectId ?? null,
        startedAt: null,
        plannedMs: plannedFor(toMode, next.settings),
        elapsedMs: 0,
        running: false,
        lastResume: null,
        interruptions: 0,
        note: '',
      };

      // Автостарт следующего интервала. Не срабатывает для интервала,
      // истёкшего при закрытой вкладке: человек ещё не видел результата,
      // и запускать за него новую работу неправильно.
      const auto =
        !opts.offline &&
        ((isBreak(toMode) && next.settings.autoStartBreak) ||
          (toMode === 'focus' && next.settings.autoStartFocus));

      if (auto) {
        const now = Date.now();
        fresh.running = true;
        fresh.startedAt = now;
        fresh.lastResume = now;
      }

      setData({ ...next, session: fresh, cyclePos });

      if (!opts.silent) playAlarm(prev.settings.alarm, prev.settings.volume);
      completionRef.current?.({
        from: fromMode,
        to: toMode,
        natural,
        offline: opts.offline === true,
        workedMs,
        entry,
      });
    },
    [bankInto, plannedFor],
  );

  // -------------------------------------------------------------------------
  // Ход времени
  // -------------------------------------------------------------------------

  const completeRef = useRef(complete);
  completeRef.current = complete;

  useEffect(() => {
    if (!running) return;

    const check = () => {
      const s = dataRef.current.session;
      if (!s?.running) return;
      if (s.mode !== 'free') {
        const left = s.plannedMs - (s.elapsedMs + (s.lastResume ? Date.now() - s.lastResume : 0));
        if (left <= 0) {
          completeRef.current(true);
          return;
        }
      }
      setPulse((p) => p + 1);
    };

    const id = setInterval(check, 250);
    // Вкладка вернулась на передний план после прореживания таймеров:
    // сверяемся с часами немедленно, а не ждём следующего интервала.
    const onVisible = () => {
      if (!document.hidden) check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [running]);

  /**
   * Восстановление после перезагрузки.
   *
   * Если интервал истёк, пока страница была закрыта, засчитывается ровно
   * запланированное время, а не весь промежуток отсутствия: человек не
   * работал те часы, что ноутбук был закрыт.
   */
  useEffect(() => {
    if (!ready) return;
    const s = dataRef.current.session;
    if (!s?.running || !s.lastResume) return;

    const finishedAt = s.lastResume + (s.plannedMs - s.elapsedMs);
    if (s.mode !== 'free' && finishedAt <= Date.now()) {
      setData((prev) =>
        prev.session
          ? {
              ...prev,
              session: {
                ...prev.session,
                elapsedMs: prev.session.plannedMs,
                running: false,
                lastResume: null,
              },
            }
          : prev,
      );
      // Даём состоянию примениться, затем закрываем интервал уже по-честному.
      setTimeout(() => {
        completeRef.current(true, { silent: true, offline: true });
      }, 0);
    }
    // Только при первой готовности: это разовое восстановление.
  }, [ready]);

  // Фоновый звук привязан к состоянию таймера, а не к кнопке настроек.
  useEffect(() => {
    if (!ready) return;
    syncAmbient(running, mode, settings.ambient, settings.volume);
  }, [ready, running, mode, settings.ambient, settings.volume]);

  // -------------------------------------------------------------------------
  // Команды таймера
  // -------------------------------------------------------------------------

  const start = useCallback(() => {
    unlockAudio(dataRef.current.settings.volume);
    setData((prev) => {
      const s = prev.session ?? freshSession('focus', null, prev.settings);
      if (s.running) return prev;
      const now = Date.now();
      return {
        ...prev,
        session: { ...s, running: true, startedAt: s.startedAt ?? now, lastResume: now },
      };
    });
  }, [freshSession]);

  const pause = useCallback(() => {
    setData((prev) => {
      const s = prev.session;
      if (!s?.running || !s.lastResume) return prev;
      return {
        ...prev,
        session: {
          ...s,
          elapsedMs: s.elapsedMs + (Date.now() - s.lastResume),
          running: false,
          lastResume: null,
        },
      };
    });
  }, []);

  const toggle = useCallback(() => {
    if (dataRef.current.session?.running) pause();
    else start();
  }, [pause, start]);

  /** Сброс. `discard` выбрасывает наработанное, не записывая в журнал. */
  const reset = useCallback(
    (discard = false) => {
      setData((prev) => {
        if (!prev.session) return prev;
        const base = discard ? prev : bankInto(prev, false).next;
        return {
          ...base,
          session: freshSession(prev.session.mode, prev.session.taskId, base.settings),
        };
      });
    },
    [bankInto, freshSession],
  );

  const skip = useCallback(() => {
    complete(false);
  }, [complete]);

  const setMode = useCallback(
    (next: TimerMode) => {
      setData((prev) => {
        const s = prev.session;
        if (s?.mode === next) return prev;
        const base = s && (s.running || s.elapsedMs > 0) ? bankInto(prev, false).next : prev;
        return { ...base, session: freshSession(next, s?.taskId ?? null, base.settings) };
      });
    },
    [bankInto, freshSession],
  );

  const setTask = useCallback((taskId: string | null) => {
    setData((prev) => {
      const s = prev.session;
      if (!s) return prev;
      const task = taskId ? prev.tasks.find((t) => t.id === taskId) : undefined;
      return { ...prev, session: { ...s, taskId, projectId: task?.projectId ?? null } };
    });
  }, []);

  const addMinutes = useCallback((min: number) => {
    setData((prev) => {
      const s = prev.session;
      if (!s || s.mode === 'free') return prev;
      return {
        ...prev,
        session: { ...s, plannedMs: Math.max(60_000, s.plannedMs + min * 60_000) },
      };
    });
  }, []);

  const addInterruption = useCallback(() => {
    setData((prev) =>
      prev.session
        ? { ...prev, session: { ...prev.session, interruptions: prev.session.interruptions + 1 } }
        : prev,
    );
  }, []);

  const setNote = useCallback((note: string) => {
    setData((prev) =>
      prev.session ? { ...prev, session: { ...prev.session, note: note.slice(0, 500) } } : prev,
    );
  }, []);

  // -------------------------------------------------------------------------
  // Задачи и проекты
  // -------------------------------------------------------------------------

  const addTask = useCallback(
    (input: { title: string; projectId?: string | null; estimate?: number }) => {
      const title = input.title.trim().slice(0, 200);
      if (!title) return null;

      const task: FlowTask = {
        id: flowUid(),
        title,
        projectId: input.projectId ?? null,
        estimate: clamp(input.estimate ?? 1, 0, 99),
        done: false,
        doneAt: null,
        createdAt: Date.now(),
        order: 0,
        notes: '',
      };

      setData((prev) => {
        // Новая задача встаёт первой: обычно её и собираются делать.
        const minOrder = prev.tasks.reduce((m, t) => Math.min(m, t.order), 0);
        return { ...prev, tasks: [...prev.tasks, { ...task, order: minOrder - 1 }] };
      });
      return task;
    },
    [],
  );

  const updateTask = useCallback((id: string, patch: Partial<FlowTask>) => {
    setData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) =>
        t.id === id
          ? {
              ...t,
              ...patch,
              doneAt: 'done' in patch ? (patch.done ? Date.now() : null) : t.doneAt,
            }
          : t,
      ),
    }));
  }, []);

  /**
   * Удаление задачи.
   *
   * Записи времени остаются: человек эти часы отработал, и стирать их
   * вместе с задачей нельзя. Ссылка просто обнуляется.
   */
  const deleteTask = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      tasks: prev.tasks.filter((t) => t.id !== id),
      entries: prev.entries.map((e) => (e.taskId === id ? { ...e, taskId: null } : e)),
      session: prev.session?.taskId === id ? { ...prev.session, taskId: null } : prev.session,
    }));
  }, []);

  const reorderTasks = useCallback((ids: string[]) => {
    setData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => {
        const i = ids.indexOf(t.id);
        return i >= 0 ? { ...t, order: i } : t;
      }),
    }));
  }, []);

  const addProject = useCallback((name: string, color?: string) => {
    const clean = name.trim().slice(0, 60);
    if (!clean) return null;

    // Идентификатор и цвет считаются заранее: обновление состояния может
    // быть выполнено дважды в строгом режиме React, и внутри него нельзя
    // порождать новые значения.
    const created: FlowProject = {
      id: flowUid(),
      name: clean,
      color: color ?? FLOW_PALETTE[0] ?? '#94a3b8',
      createdAt: Date.now(),
    };

    setData((prev) => ({
      ...prev,
      projects: [
        ...prev.projects,
        color
          ? created
          : {
              ...created,
              color: FLOW_PALETTE[prev.projects.length % FLOW_PALETTE.length] ?? created.color,
            },
      ],
    }));
    return created;
  }, []);

  const updateProject = useCallback((id: string, patch: Partial<FlowProject>) => {
    setData((prev) => ({
      ...prev,
      projects: prev.projects.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    }));
  }, []);

  const deleteProject = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      projects: prev.projects.filter((p) => p.id !== id),
      tasks: prev.tasks.map((t) => (t.projectId === id ? { ...t, projectId: null } : t)),
      entries: prev.entries.map((e) => (e.projectId === id ? { ...e, projectId: null } : e)),
    }));
  }, []);

  // -------------------------------------------------------------------------
  // Журнал
  // -------------------------------------------------------------------------

  const addEntry = useCallback((input: Omit<FlowEntry, 'id'>) => {
    if (input.end <= input.start) return null;
    const entry: FlowEntry = { ...input, id: flowUid() };
    setData((prev) => ({
      ...prev,
      entries: [...prev.entries, entry].sort((a, b) => a.start - b.start),
    }));
    return entry;
  }, []);

  const updateEntry = useCallback((id: string, patch: Partial<FlowEntry>) => {
    setData((prev) => ({
      ...prev,
      entries: prev.entries
        .map((e) => (e.id === id ? { ...e, ...patch } : e))
        .filter((e) => e.end > e.start)
        .sort((a, b) => a.start - b.start),
    }));
  }, []);

  const deleteEntry = useCallback((id: string) => {
    setData((prev) => ({ ...prev, entries: prev.entries.filter((e) => e.id !== id) }));
  }, []);

  const restoreEntry = useCallback((entry: FlowEntry) => {
    setData((prev) => ({
      ...prev,
      entries: [...prev.entries, entry].sort((a, b) => a.start - b.start),
    }));
  }, []);

  // -------------------------------------------------------------------------
  // Настройки и данные целиком
  // -------------------------------------------------------------------------

  const updateSettings = useCallback((patch: Partial<FlowSettings>) => {
    setData((prev) => {
      const settingsNext = { ...prev.settings, ...patch };
      const s = prev.session;
      // Новая длительность применяется к незапущенной сессии сразу:
      // иначе человек меняет 25 на 50 и не понимает, почему на циферблате
      // по-прежнему 25.
      const idle = s && !s.running && s.elapsedMs === 0;
      const plannedNext =
        s && idle
          ? s.mode === 'focus'
            ? settingsNext.focusMin * 60_000
            : s.mode === 'short'
              ? settingsNext.shortMin * 60_000
              : s.mode === 'long'
                ? settingsNext.longMin * 60_000
                : 0
          : s?.plannedMs;

      return {
        ...prev,
        settings: settingsNext,
        session: s ? { ...s, plannedMs: plannedNext ?? s.plannedMs } : s,
      };
    });
  }, []);

  const importData = useCallback((incoming: unknown, merge: boolean) => {
    const parsed = migrate(incoming);
    let added = 0;

    setData((prev) => {
      if (!merge) {
        added = parsed.entries.length;
        return { ...parsed, session: null };
      }

      // Совпадение по идентификатору ловит повторный импорт того же файла,
      // совпадение по времени и режиму — запись, созданную на другом
      // устройстве до обмена файлами.
      const ids = new Set(prev.entries.map((e) => e.id));
      const signatures = new Set(
        prev.entries.map((e) => `${String(e.start)}|${String(e.end)}|${e.mode}`),
      );
      const fresh = parsed.entries.filter(
        (e) => !ids.has(e.id) && !signatures.has(`${String(e.start)}|${String(e.end)}|${e.mode}`),
      );
      added = fresh.length;

      const projectIds = new Set(prev.projects.map((p) => p.id));
      const taskIds = new Set(prev.tasks.map((t) => t.id));

      return {
        ...prev,
        projects: [...prev.projects, ...parsed.projects.filter((p) => !projectIds.has(p.id))],
        tasks: [...prev.tasks, ...parsed.tasks.filter((t) => !taskIds.has(t.id))],
        entries: [...prev.entries, ...fresh].sort((a, b) => a.start - b.start),
      };
    });

    return added;
  }, []);

  const wipe = useCallback(() => {
    setData(emptyData());
  }, []);

  // -------------------------------------------------------------------------

  const todayKey = dayKey();
  const taskById = useCallback(
    (id: string | null) => (id ? (data.tasks.find((t) => t.id === id) ?? null) : null),
    [data.tasks],
  );
  const projectById = useCallback(
    (id: string | null) => (id ? (data.projects.find((p) => p.id === id) ?? null) : null),
    [data.projects],
  );

  /** Сколько помидоров уже засчитано задаче. */
  const taskPomos = useCallback(
    (id: string) =>
      data.entries.filter((e) => e.taskId === id && e.mode === 'focus' && e.completed).length,
    [data.entries],
  );

  const taskSeconds = useCallback(
    (id: string) =>
      data.entries
        .filter((e) => e.taskId === id && e.mode === 'focus')
        .reduce((s, e) => s + (e.end - e.start) / 1000, 0),
    [data.entries],
  );

  return {
    ready,
    recovered,
    data,
    settings,
    session,
    mode,
    running,
    active,
    elapsedMs,
    remainingMs,
    progress,
    todayKey,
    defaults: DEFAULT_SETTINGS,

    onCompletion,
    start,
    pause,
    toggle,
    reset,
    skip,
    complete,
    setMode,
    setTask,
    addMinutes,
    addInterruption,
    setNote,

    addTask,
    updateTask,
    deleteTask,
    reorderTasks,
    addProject,
    updateProject,
    deleteProject,

    addEntry,
    updateEntry,
    deleteEntry,
    restoreEntry,

    updateSettings,
    importData,
    wipe,

    taskById,
    projectById,
    taskPomos,
    taskSeconds,
  };
}

export type FlowTimeApi = ReturnType<typeof useFlowTime>;
